import React, { useState, useEffect, useRef } from "react";
import useAdminApi from "@/hooks/useAdminApi";
import useAdminMutation from "@/hooks/useAdminMutation";
import useRLContext from "@/hooks/useRLContext";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * TicketCreateDialog — SS-2 (Feb 2026) + SS-2.1 (Jun 2026).
 *
 * Admin-only / preview-first dialog to create a new Racket Lab ticket.
 * Calls `POST /api/admin/racket-lab/tickets` with the RL-A.6 payload
 * extended by the SS-2.1 stringing-specific optional fields:
 *   equipment_product_type / brand / model / accessory,
 *   string_main_label / string_cross_label,
 *   tension_main / tension_cross / tension_unit (kg default),
 *   string_origin (shop|customer) + string_supply_format (set|reel),
 *   customer_string_notes, created_for_profile_type.
 *
 * Brand/model selectors are fed by the admin-only catalog mock
 * (`GET /api/admin/racket-lab/catalog-mock`) — NO real Catalog
 * Intelligence integration yet; "Other" enables controlled free text.
 *
 * Form state lives inside `TicketCreateDialogInner` which is only
 * mounted when `open=true`, so closing the dialog auto-resets the form.
 *
 * HARD BLOCKS preserved: no customer-facing, no ConsentGate, no Q28
 * emails, no notification dispatch, no public scan, no production.
 */
const OTHER = "__other__";
const PRODUCT_TYPES = ["racket", "padel_racket", "accessory", "other"];
const PROFILE_TYPES = ["player", "parent_guardian", "coach", "stringer", "shop_operator", "admin_staff"];

// ---------------------------------------------------------------------------
// GEAR-3 (Jun 2026) — My Gear autofill MOCK (admin/staff preview-only).
//
// Inline mock data mirroring `/admin/gear/preview` (GEAR-2). Selecting a
// racket fills empty fields ONLY, respecting the canonical hierarchy:
//   1. Manual override (operator-typed value)              ← TOP wins
//   2. Customer/Racket profile (THIS GEAR-3 mock)
//   3. Shop preset (ROLE-2)
//   4. Operator preset (ROLE-4 / 4B)
//   5. Blank
//
// Zero DB writes. Zero API calls. Zero customer data. Zero consent capture.
// The block is collapsible and hidden by default behind a localStorage flag.
// ---------------------------------------------------------------------------
const GEAR_MOCK_RACKETS = [
  {
    id: "RKT-MOCK-001",
    slot: "default",
    label: "Match · default",
    display_brand: "Wilson",
    display_model: "Blade 98 v9",
    grip_size: "L3",
    setup: {
      display_vertical: "Luxilon Alu Power 125",
      display_horizontal: "Wilson NXT 130",
      tension_vertical: "24.0",
      tension_horizontal: "23.0",
      tension_unit: "kg",
      hybrid: true,
      notes: "Hybrid control + comfort. Anna prefiere ±0.5 kg.",
    },
  },
  {
    id: "RKT-MOCK-002",
    slot: "racket_2",
    label: "Entrenamiento",
    display_brand: "Wilson",
    display_model: "Clash 100 v3",
    grip_size: "L3",
    setup: {
      display_vertical: "Wilson NXT 130 (full bed)",
      display_horizontal: "",
      tension_vertical: "22.0",
      tension_horizontal: "22.0",
      tension_unit: "kg",
      hybrid: false,
      notes: "Full multifilamento; sesión técnica larga.",
    },
  },
  {
    id: "RKT-MOCK-003",
    slot: "racket_3",
    label: "Reserva lluvia · pending canonical",
    display_brand: "Prince",
    display_model: "Phantom 100X 305",
    grip_size: "L3",
    setup: {
      display_vertical: "Tecnifibre Black Code 125 (pending)",
      display_horizontal: "",
      tension_vertical: "23.0",
      tension_horizontal: "23.0",
      tension_unit: "kg",
      hybrid: false,
      notes: "Cordaje fallback free-text — pending canonical upgrade.",
    },
  },
];

const GEAR_LS_TOGGLE = "rl.gear3.show_seed_mock";

const _Field = ({ label, htmlFor, children, hint, testId, className = "" }) => (
  <label data-testid={testId} htmlFor={htmlFor} className={`flex flex-col gap-1 ${className}`}>
    <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
    {children}
    {hint ? <span className="text-[10px] text-slate-400">{hint}</span> : null}
  </label>
);

const _SectionTitle = ({ children, testId }) => (
  <div data-testid={testId} className="md:col-span-2 mt-1 border-t border-slate-100 pt-2">
    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">{children}</span>
  </div>
);

const INPUT_CLS = "rounded-md border border-slate-300 px-2 py-1.5 text-sm";

const TicketCreateDialogInner = ({ onClose, onCreated }) => {
  const t = useRacketLabAdminT();
  const { data: shopsData } = useAdminApi("/admin/racket-lab/shops");
  const { data: capsData } = useAdminApi("/admin/racket-lab/capabilities");
  const { data: catalogData } = useAdminApi("/admin/racket-lab/catalog-mock");
  const { run, loading: submitting } = useAdminMutation();

  const shops =
    shopsData?.items || shopsData?.shops || (Array.isArray(shopsData) ? shopsData : []);
  const capabilities =
    capsData?.items || capsData?.capabilities || (Array.isArray(capsData) ? capsData : []);
  const catalogBrands = catalogData?.brands || [];
  const catalogStrings = catalogData?.strings || [];

  const [form, setForm] = useState({
    racket_lab_shop_id: "",
    capability_key: "",
    customer_reference_label: "",
    equipment_reference_label: "",
    equipment_notes: "",
    racket_lab_operator_id: "",
    racket_lab_machine_id: "",
    // SS-2.1 — stringing-specific.
    equipment_product_type: "",
    equipment_brand_key: "",
    equipment_brand_custom: "",
    equipment_model_choice: "",
    equipment_model_custom: "",
    equipment_accessory_label: "",
    string_main_label: "",
    string_cross_label: "",
    string_main_catalog_product_id: "",
    string_cross_catalog_product_id: "",
    tension_main: "",
    tension_cross: "",
    tension_unit: "kg",
    string_origin: "",
    string_supply_format: "",
    customer_string_notes: "",
    created_for_profile_type: "",
    racket_lab_inventory_item_id: "",
    scheduled_for: "",
  });
  const [error, setError] = useState(null);

  // SS-6 — shop inventory (sets/reels) for the optional ticket link.
  // Skipped for qa_tester (operational inventory is outside their surface).
  const { ctx: rlCtx } = useRLContext();
  const { data: invData } = useAdminApi("/admin/racket-lab/inventory", {
    skip: !form.racket_lab_shop_id || rlCtx?.profile === "qa_tester",
    params: { racket_lab_shop_id: form.racket_lab_shop_id, status: "active", limit: 200 },
  });
  const inventoryItems = invData?.items || [];

  // RACKET-LAB-ROLE-4A (Feb 2026) — operators scoped to the selected
  // shop. Replaces the legacy free-text UUID input with a typeahead/
  // select populated by GET /admin/racket-lab/operators?status=active
  // &racket_lab_shop_id={shop}. Anti-enumeration: skipped until a shop
  // is chosen; qa_tester is excluded because operational workforce is
  // outside their surface. A "manual UUID" fallback toggle stays
  // available so any pre-existing workflow keeps working unchanged.
  //
  // RACKET-LAB-ROLE-4C (Feb 2026) — promoted to an actual debounced
  // typeahead with a server-side `q` filter. We keep the same hook
  // call shape so ROLE-4A / ROLE-4B regression stays intact: when
  // there is no query the endpoint returns the full active list for
  // the shop, exactly as before. The selected operator is snapshotted
  // in `selectedOperator` so the chip survives even if the user
  // narrows the search and the option drops out of the current page.
  const [operatorSearch, setOperatorSearch] = useState("");
  const [operatorSearchDebounced, setOperatorSearchDebounced] = useState("");
  useEffect(() => {
    const h = setTimeout(() => setOperatorSearchDebounced(operatorSearch), 250);
    return () => clearTimeout(h);
  }, [operatorSearch]);
  const { data: opsData, loading: opsLoading } = useAdminApi(
    "/admin/racket-lab/operators",
    {
      skip: !form.racket_lab_shop_id || rlCtx?.profile === "qa_tester",
      params: {
        racket_lab_shop_id: form.racket_lab_shop_id,
        status: "active",
        limit: 20,
        ...(operatorSearchDebounced.trim()
          ? { q: operatorSearchDebounced.trim() }
          : {}),
      },
    },
  );
  const operatorOptions = opsData?.items || opsData?.operators || [];
  const [operatorManualMode, setOperatorManualMode] = useState(false);
  const [operatorDropdownOpen, setOperatorDropdownOpen] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const operatorContainerRef = useRef(null);
  useEffect(() => {
    if (!operatorDropdownOpen) return undefined;
    const onDocClick = (ev) => {
      if (
        operatorContainerRef.current &&
        !operatorContainerRef.current.contains(ev.target)
      ) {
        setOperatorDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [operatorDropdownOpen]);

  // CATALOG-4 (Jun 2026) — CENTRAL catalog read-only consumption (tennis
  // rackets + strings, modules: racket_lab, active). The mock above stays
  // as technical fallback (padel/other + manual). qa_tester: no catalog.
  const { data: centralData } = useAdminApi("/admin/racket-lab/catalog/products", {
    skip: rlCtx?.profile === "qa_tester",
    params: { limit: 500 },
  });
  const centralProducts = centralData?.items || [];
  const centralRackets = centralProducts.filter((p) => p.type === "racket");
  const centralStrings = centralProducts.filter((p) => p.type === "string");
  const centralRacketBrands = [...new Map(
    centralRackets.map((p) => [p.brand_id, { brand_id: p.brand_id, brand_name: p.brand_name }]),
  ).values()];

  // RACKET-LAB-ROLE-2 (Feb 2026) — autofill tension defaults from the
  // selected shop's settings preset. Applied SYNCHRONOUSLY when the
  // shop changes; manual edits always win because we only set values
  // when the form fields are still empty at the time of selection.
  // RACKET-LAB-ROLE-4B (Feb 2026) — same additive autofill from the
  // selected operator's preset, only filling fields the shop preset
  // (ROLE-2) or manual edits did not already populate. Both banners
  // surface independently so the operator can see provenance.
  const [shopPresetApplied, setShopPresetApplied] = useState(false);
  const [operatorPresetApplied, setOperatorPresetApplied] = useState(false);

  // GEAR-3 (Jun 2026) — My Gear autofill MOCK state. Persisted to
  // localStorage so toggling reloads survive (admin-only ergonomic
  // detail; no PII, no DB).
  const [gearMockEnabled, setGearMockEnabled] = useState(() => {
    try {
      return window.localStorage.getItem(GEAR_LS_TOGGLE) === "1";
    } catch {
      return false;
    }
  });
  const [gearAppliedRacketId, setGearAppliedRacketId] = useState(null);
  const [gearApplyReport, setGearApplyReport] = useState(null);
  useEffect(() => {
    try {
      window.localStorage.setItem(GEAR_LS_TOGGLE, gearMockEnabled ? "1" : "0");
    } catch {
      /* localStorage may be unavailable in some preview contexts */
    }
  }, [gearMockEnabled]);

  // Applies a GEAR-3 mock racket suggestion to the form, ONLY filling
  // fields currently empty. Manual override, shop preset (ROLE-2) and
  // operator preset (ROLE-4/4B) all WIN over this layer because they
  // either already wrote a value (we skip) or were applied first.
  const applyGearSuggestion = (racket) => {
    setForm((s) => {
      const next = { ...s };
      const filled = [];
      const preserved = [];
      const setIfEmpty = (key, value, label) => {
        if (value == null || String(value).trim() === "") return;
        if (s[key] != null && String(s[key]).trim() !== "") {
          preserved.push(label || key);
          return;
        }
        next[key] = String(value);
        filled.push(label || key);
      };
      // Product type defaults to racket for any GEAR-3 mock.
      setIfEmpty("equipment_product_type", "racket", "product_type");
      setIfEmpty("equipment_brand_custom", racket.display_brand, "brand");
      setIfEmpty("equipment_model_custom", racket.display_model, "model");
      setIfEmpty("equipment_reference_label", `${racket.display_brand} ${racket.display_model}`, "equipment_ref");
      const su = racket.setup || {};
      setIfEmpty("string_main_label", su.display_vertical, "main_string");
      if (su.hybrid) {
        setIfEmpty("string_cross_label", su.display_horizontal, "cross_string");
      }
      setIfEmpty("tension_main", su.tension_vertical, "tension_main");
      setIfEmpty("tension_cross", su.tension_horizontal, "tension_cross");
      // Unit only flips when BOTH tensions were empty pre-apply (mirrors
      // ROLE-2 / ROLE-4B semantics).
      if (
        su.tension_unit &&
        s.tension_main === "" &&
        s.tension_cross === ""
      ) {
        next.tension_unit = su.tension_unit;
        filled.push("tension_unit");
      } else if (su.tension_unit) {
        preserved.push("tension_unit");
      }
      setIfEmpty("customer_string_notes", su.notes, "notes");
      setGearAppliedRacketId(racket.id);
      setGearApplyReport({ filled, preserved });
      return next;
    });
  };
  const clearGearSuggestion = () => {
    setGearAppliedRacketId(null);
    setGearApplyReport(null);
  };

  const onShopChange = (shopId) => {
    setForm((s) => {
      const next = { ...s, racket_lab_shop_id: shopId };
      // RACKET-LAB-ROLE-4A — when the shop changes, the previously
      // selected operator may no longer belong to the new shop. We
      // clear it pre-emptively; the typeahead/select will repopulate
      // with operators of the new shop (manual mode keeps whatever
      // UUID the admin typed if the toggle is active).
      if (!operatorManualMode && s.racket_lab_operator_id) {
        next.racket_lab_operator_id = "";
        // ROLE-4B — clearing the operator drops any operator banner;
        // any tension values already typed/applied stay (no overwrite
        // of manual or shop-driven values, by design).
        setOperatorPresetApplied(false);
        // ROLE-4C — also drop the cached selected snapshot + reset
        // the typeahead query so the dropdown for the new shop opens
        // clean.
        setSelectedOperator(null);
        setOperatorSearch("");
        setOperatorSearchDebounced("");
        setOperatorDropdownOpen(false);
      }
      const sh = shops.find((x) => x.id === shopId);
      const p = sh?.settings || {};
      const hasPreset =
        p.default_tension_unit != null ||
        p.default_main_tension != null ||
        p.default_cross_tension != null;
      if (!hasPreset) {
        setShopPresetApplied(false);
        return next;
      }
      let touched = false;
      if (p.default_main_tension != null && s.tension_main === "") {
        next.tension_main = String(p.default_main_tension);
        touched = true;
      }
      if (p.default_cross_tension != null && s.tension_cross === "") {
        next.tension_cross = String(p.default_cross_tension);
        touched = true;
      }
      if (
        p.default_tension_unit &&
        s.tension_main === "" &&
        s.tension_cross === ""
      ) {
        next.tension_unit = p.default_tension_unit;
        touched = true;
      }
      setShopPresetApplied(touched);
      return next;
    });
  };

  // RACKET-LAB-ROLE-4B (Feb 2026) — operator preset autofill.
  // Tension hierarchy enforced:
  //   1. manual edit (always wins — we only write when field is empty)
  //   2. future customer/racket preset (HARD BLOCKED — not wired)
  //   3. shop preset (ROLE-2 — applied on shop change above)
  //   4. operator preset (ROLE-4 — applied here on operator change)
  //   5. empty
  // Manual-UUID fallback (operatorManualMode === true) skips autofill
  // because the raw UUID does not carry preset data on the client.
  const onOperatorChange = (operatorId, operatorSnapshot = null) => {
    setForm((s) => {
      const next = { ...s, racket_lab_operator_id: operatorId };
      if (operatorManualMode) {
        // Legacy fallback — keep behaviour intact, no autofill.
        setSelectedOperator(null);
        return next;
      }
      if (!operatorId) {
        // Operator cleared → drop the operator banner. Any tension
        // values already set (manual or shop preset) stay untouched.
        setOperatorPresetApplied(false);
        setSelectedOperator(null);
        return next;
      }
      // ROLE-4C — prefer the snapshot passed in by the typeahead
      // (it is the option the user actually clicked); fall back to
      // the in-memory page when called without a snapshot (e.g. a
      // legacy programmatic update). If neither resolves we still
      // commit the id so the form survives.
      const op =
        operatorSnapshot ||
        operatorOptions.find((o) => o.id === operatorId) ||
        null;
      setSelectedOperator(op);
      if (!op) {
        setOperatorPresetApplied(false);
        return next;
      }
      let touched = false;
      if (op.default_main_tension != null && s.tension_main === "") {
        next.tension_main = String(op.default_main_tension);
        touched = true;
      }
      if (op.default_cross_tension != null && s.tension_cross === "") {
        next.tension_cross = String(op.default_cross_tension);
        touched = true;
      }
      // Unit only flips when BOTH tensions were empty at the time of
      // selection, matching the ROLE-2 shop-preset semantics. This
      // prevents stomping a unit chosen alongside a manual tension
      // value.
      if (
        op.default_tension_unit &&
        s.tension_main === "" &&
        s.tension_cross === ""
      ) {
        next.tension_unit = op.default_tension_unit;
        touched = true;
      }
      setOperatorPresetApplied(touched);
      return next;
    });
  };

  const required_ok =
    form.racket_lab_shop_id.trim().length > 0 &&
    form.capability_key.trim().length > 0 &&
    form.equipment_reference_label.trim().length > 0;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const visibleBrands = form.equipment_product_type
    ? catalogBrands.filter(
        (b) => b.key === "other" || (b.product_types || []).includes(form.equipment_product_type),
      )
    : catalogBrands;
  // CATALOG-4 — central path markers (cat: prefix keeps mock keys intact).
  const isCatBrand = form.equipment_brand_key.startsWith("cat:");
  const catBrandId = isCatBrand ? form.equipment_brand_key.slice(4) : null;
  const catBrandRackets = catBrandId
    ? centralRackets.filter((p) => p.brand_id === catBrandId)
    : [];
  const catSelectedRacket = form.equipment_model_choice.startsWith("cat:")
    ? centralRackets.find((p) => p.catalog_product_id === form.equipment_model_choice.slice(4))
    : null;
  const selectedBrand = catalogBrands.find((b) => b.key === form.equipment_brand_key);
  const brandLabel = isCatBrand
    ? (centralRacketBrands.find((b) => b.brand_id === catBrandId)?.brand_name || "")
    : form.equipment_brand_key === "other"
      ? form.equipment_brand_custom.trim()
      : selectedBrand?.label || "";
  const modelLabel = catSelectedRacket
    ? catSelectedRacket.label
    : form.equipment_model_choice === OTHER
      ? form.equipment_model_custom.trim()
      : form.equipment_model_choice.startsWith("cat:")
        ? ""
        : form.equipment_model_choice;

  const onBrandChange = (v) =>
    setForm((s) => ({ ...s, equipment_brand_key: v, equipment_model_choice: "", equipment_model_custom: "" }));

  const onModelChange = (v) =>
    setForm((s) => {
      const next = { ...s, equipment_model_choice: v };
      if (v && v !== OTHER && !s.equipment_reference_label.trim()) {
        const isCat = s.equipment_brand_key.startsWith("cat:");
        const b = isCat
          ? centralRacketBrands.find((x) => x.brand_id === s.equipment_brand_key.slice(4))?.brand_name || ""
          : s.equipment_brand_key === "other"
            ? s.equipment_brand_custom.trim()
            : catalogBrands.find((x) => x.key === s.equipment_brand_key)?.label || "";
        const m = v.startsWith("cat:")
          ? centralRackets.find((p) => p.catalog_product_id === v.slice(4))?.label || ""
          : v;
        next.equipment_reference_label = m.includes(b) ? m : [b, m].filter(Boolean).join(" ");
      }
      return next;
    });

  // CATALOG-4 — picking a catalog string fills the legacy label (manual
  // fallback intact: editing the text afterwards clears the additive ref).
  const onStringCatalogPick = (side, productId) =>
    setForm((s) => {
      const p = centralStrings.find((x) => x.catalog_product_id === productId);
      return {
        ...s,
        [`string_${side}_catalog_product_id`]: p ? p.catalog_product_id : "",
        [`string_${side}_label`]: p ? p.label.slice(0, 80) : s[`string_${side}_label`],
      };
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!required_ok || submitting) return;
    setError(null);
    const payload = {
      racket_lab_shop_id: form.racket_lab_shop_id.trim(),
      capability_key: form.capability_key.trim(),
      equipment_reference_label: form.equipment_reference_label.trim(),
    };
    if (form.customer_reference_label.trim()) payload.customer_reference_label = form.customer_reference_label.trim();
    if (form.equipment_notes.trim()) payload.equipment_notes = form.equipment_notes.trim();
    if (form.racket_lab_operator_id.trim()) payload.racket_lab_operator_id = form.racket_lab_operator_id.trim();
    if (form.racket_lab_machine_id.trim()) payload.racket_lab_machine_id = form.racket_lab_machine_id.trim();
    // SS-2.1 — stringing-specific structured fields (never in equipment_notes).
    if (form.equipment_product_type) payload.equipment_product_type = form.equipment_product_type;
    if (brandLabel) payload.equipment_brand_label = brandLabel;
    if (modelLabel) payload.equipment_model_label = modelLabel;
    // CATALOG-4 — additive central-catalog refs (only when selected).
    if (catSelectedRacket) payload.equipment_catalog_product_id = catSelectedRacket.catalog_product_id;
    if (form.string_main_catalog_product_id) payload.string_main_catalog_product_id = form.string_main_catalog_product_id;
    if (form.string_cross_catalog_product_id) payload.string_cross_catalog_product_id = form.string_cross_catalog_product_id;
    if (form.equipment_accessory_label.trim()) payload.equipment_accessory_label = form.equipment_accessory_label.trim();
    if (form.string_main_label.trim()) payload.string_main_label = form.string_main_label.trim();
    if (form.string_cross_label.trim()) payload.string_cross_label = form.string_cross_label.trim();
    if (form.tension_main !== "") payload.tension_main = parseInt(form.tension_main, 10);
    if (form.tension_cross !== "") payload.tension_cross = parseInt(form.tension_cross, 10);
    if (form.tension_main !== "" || form.tension_cross !== "") payload.tension_unit = form.tension_unit;
    if (form.string_origin) payload.string_origin = form.string_origin;
    if (form.string_origin === "shop" && form.racket_lab_inventory_item_id) {
      payload.racket_lab_inventory_item_id = form.racket_lab_inventory_item_id;
    }
    if (form.string_origin === "customer") {
      if (form.string_supply_format) payload.string_supply_format = form.string_supply_format;
      if (form.customer_string_notes.trim()) payload.customer_string_notes = form.customer_string_notes.trim();
    }
    if (form.created_for_profile_type) payload.created_for_profile_type = form.created_for_profile_type;
    if (form.scheduled_for) payload.scheduled_for = form.scheduled_for;

    const result = await run("post", "/admin/racket-lab/tickets", payload).catch((err) => {
      setError(
        err?.detail
          ? typeof err.detail === "string"
            ? err.detail
            : JSON.stringify(err.detail)
          : err?.message || t("tickets.create.error_unknown"),
      );
      return null;
    });
    if (result && result.id) {
      onCreated?.(result);
    }
  };

  return (
    <div className="flex max-h-[88vh] w-full max-w-3xl flex-col rounded-lg border border-slate-200 bg-white shadow-lg">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900">{t("tickets.create.title")}</h2>
        <button
          type="button"
          data-testid="rl-ticket-create-close"
          onClick={onClose}
          className="text-xs text-slate-500 hover:text-slate-900"
        >
          {t("tickets.create.close")}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-3 overflow-y-auto px-4 py-4 md:grid-cols-2">
        {/* GEAR-3 (Jun 2026) — My Gear autofill MOCK · admin/staff preview */}
        <section
          data-testid="rl-ticket-create-gear3-block"
          className="md:col-span-2 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3"
        >
          <header className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-emerald-900">
                {t("tickets.create.gear3.title")}
              </span>
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800">
                {t("tickets.create.gear3.preview_badge")}
              </span>
            </div>
            <label
              className="flex items-center gap-1.5 text-[11px] text-slate-700"
              data-testid="rl-ticket-create-gear3-toggle-label"
            >
              <input
                type="checkbox"
                data-testid="rl-ticket-create-gear3-toggle"
                checked={gearMockEnabled}
                onChange={(e) => {
                  setGearMockEnabled(e.target.checked);
                  if (!e.target.checked) clearGearSuggestion();
                }}
              />
              {t("tickets.create.gear3.toggle")}
            </label>
          </header>

          {gearMockEnabled ? (
            <>
              <p
                data-testid="rl-ticket-create-gear3-banner"
                className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-[10px] text-amber-900"
              >
                {t("tickets.create.gear3.banner")}
              </p>
              <ul
                data-testid="rl-ticket-create-gear3-rackets"
                className="mt-2 grid gap-2 md:grid-cols-3"
              >
                {GEAR_MOCK_RACKETS.map((r) => {
                  const applied = gearAppliedRacketId === r.id;
                  return (
                    <li
                      key={r.id}
                      data-testid={`rl-ticket-create-gear3-racket-${r.id}`}
                      className={`rounded border p-2 text-[11px] ${
                        applied
                          ? "border-emerald-400 bg-emerald-50"
                          : "border-slate-200 bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <span className="font-mono text-[10px] text-slate-500">
                          {r.id}
                        </span>
                        {r.slot === "default" ? (
                          <span className="rounded bg-emerald-100 px-1 text-[10px] text-emerald-800">
                            default
                          </span>
                        ) : (
                          <span className="rounded bg-slate-100 px-1 text-[10px] text-slate-700">
                            {r.slot}
                          </span>
                        )}
                        {applied ? (
                          <span className="ml-auto rounded bg-emerald-600 px-1 text-[10px] font-medium text-white">
                            applied
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-1 text-xs font-semibold text-slate-900">
                        {r.display_brand} · {r.display_model}
                      </p>
                      <p className="text-[10px] text-slate-500">
                        {r.label} · grip {r.grip_size}
                      </p>
                      <p className="mt-1 text-[10px] text-slate-600">
                        {r.setup.display_vertical}
                        {r.setup.hybrid ? ` / ${r.setup.display_horizontal}` : ""}
                        {" · "}
                        {r.setup.tension_vertical}
                        {r.setup.hybrid ? `/${r.setup.tension_horizontal}` : ""}{" "}
                        {r.setup.tension_unit}
                      </p>
                      <button
                        type="button"
                        data-testid={`rl-ticket-create-gear3-apply-${r.id}`}
                        onClick={() => applyGearSuggestion(r)}
                        className="mt-2 w-full rounded bg-emerald-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-emerald-700"
                      >
                        {t("tickets.create.gear3.apply")}
                      </button>
                    </li>
                  );
                })}
              </ul>
              {gearApplyReport ? (
                <div
                  data-testid="rl-ticket-create-gear3-report"
                  className="mt-2 rounded border border-slate-200 bg-white p-2 text-[10px]"
                >
                  <p className="font-medium text-slate-800">
                    {t("tickets.create.gear3.report.title")}
                  </p>
                  <p data-testid="rl-ticket-create-gear3-report-filled" className="text-emerald-800">
                    {t("tickets.create.gear3.report.filled")}:{" "}
                    {gearApplyReport.filled.length
                      ? gearApplyReport.filled.join(", ")
                      : "—"}
                  </p>
                  <p data-testid="rl-ticket-create-gear3-report-preserved" className="text-slate-600">
                    {t("tickets.create.gear3.report.preserved")}:{" "}
                    {gearApplyReport.preserved.length
                      ? gearApplyReport.preserved.join(", ")
                      : "—"}
                  </p>
                  <p className="mt-1 text-[10px] italic text-slate-500">
                    {t("tickets.create.gear3.report.hierarchy_note")}
                  </p>
                </div>
              ) : null}
            </>
          ) : (
            <p
              data-testid="rl-ticket-create-gear3-disabled"
              className="mt-1 text-[10px] italic text-slate-500"
            >
              {t("tickets.create.gear3.disabled_hint")}
            </p>
          )}
        </section>

        <_Field testId="rl-ticket-create-field-shop" label={t("tickets.create.shop_label")} htmlFor="rl-ticket-create-shop">
          <select
            id="rl-ticket-create-shop"
            data-testid="rl-ticket-create-shop-select"
            value={form.racket_lab_shop_id}
            onChange={(e) => onShopChange(e.target.value)}
            className={INPUT_CLS}
            required
          >
            <option value="">{t("tickets.create.shop_placeholder")}</option>
            {shops.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || s.id}
                {s.status && s.status !== "active" ? ` · ${s.status}` : ""}
              </option>
            ))}
          </select>
        </_Field>

        <_Field testId="rl-ticket-create-field-capability" label={t("tickets.create.capability_label")} htmlFor="rl-ticket-create-capability">
          <select
            id="rl-ticket-create-capability"
            data-testid="rl-ticket-create-capability-select"
            value={form.capability_key}
            onChange={(e) => set("capability_key", e.target.value)}
            className={INPUT_CLS}
            required
          >
            <option value="">{t("tickets.create.capability_placeholder")}</option>
            {capabilities.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label_default || c.key}
              </option>
            ))}
          </select>
        </_Field>

        {/* ============ SS-2.1 · Product / material ============ */}
        <_SectionTitle testId="rl-ticket-create-section-product">
          {t("tickets.create.section_product_title")}
        </_SectionTitle>
        <p data-testid="rl-ticket-create-catalog-mock-hint" className="md:col-span-2 -mt-2 text-[10px] text-amber-700">
          {t("tickets.create.catalog_mock_hint")}
        </p>

        <_Field testId="rl-ticket-create-field-product-type" label={t("tickets.create.product_type_label")} htmlFor="rl-ticket-create-product-type">
          <select
            id="rl-ticket-create-product-type"
            data-testid="rl-ticket-create-product-type-select"
            value={form.equipment_product_type}
            onChange={(e) => setForm((s) => ({ ...s, equipment_product_type: e.target.value, equipment_brand_key: "", equipment_model_choice: "", equipment_model_custom: "" }))}
            className={INPUT_CLS}
          >
            <option value="">{t("tickets.create.product_type_placeholder")}</option>
            {PRODUCT_TYPES.map((p) => (
              <option key={p} value={p}>{t(`tickets.stringing.product_type_${p}`)}</option>
            ))}
          </select>
        </_Field>

        <_Field testId="rl-ticket-create-field-brand" label={t("tickets.create.brand_label")} htmlFor="rl-ticket-create-brand">
          <select
            id="rl-ticket-create-brand"
            data-testid="rl-ticket-create-brand-select"
            value={form.equipment_brand_key}
            onChange={(e) => onBrandChange(e.target.value)}
            className={INPUT_CLS}
          >
            <option value="">{t("tickets.create.brand_placeholder")}</option>
            {form.equipment_product_type === "racket" && centralRacketBrands.map((b) => (
              <option key={`cat:${b.brand_id}`} value={`cat:${b.brand_id}`}>
                {b.brand_name}
              </option>
            ))}
            {visibleBrands
              .filter((b) => !(form.equipment_product_type === "racket"
                && centralRacketBrands.some((cb) => cb.brand_name === b.label)))
              .map((b) => (
                <option key={b.key} value={b.key}>
                  {b.key === "other" ? t("tickets.create.option_other") : b.label}
                </option>
              ))}
          </select>
        </_Field>

        {form.equipment_brand_key === "other" ? (
          <_Field testId="rl-ticket-create-field-brand-custom" label={t("tickets.create.brand_custom_label")} htmlFor="rl-ticket-create-brand-custom" hint={t("tickets.create.anti_pii_hint")}>
            <input
              id="rl-ticket-create-brand-custom"
              data-testid="rl-ticket-create-brand-custom-input"
              type="text"
              value={form.equipment_brand_custom}
              onChange={(e) => set("equipment_brand_custom", e.target.value)}
              placeholder={t("tickets.create.brand_custom_placeholder")}
              maxLength={80}
              className={INPUT_CLS}
            />
          </_Field>
        ) : null}

        <_Field testId="rl-ticket-create-field-model" label={t("tickets.create.model_label")} htmlFor="rl-ticket-create-model">
          <select
            id="rl-ticket-create-model"
            data-testid="rl-ticket-create-model-select"
            value={form.equipment_model_choice}
            onChange={(e) => onModelChange(e.target.value)}
            className={INPUT_CLS}
            disabled={!form.equipment_brand_key}
          >
            <option value="">{t("tickets.create.model_placeholder")}</option>
            {isCatBrand
              ? catBrandRackets.map((p) => (
                  <option key={`cat:${p.catalog_product_id}`} value={`cat:${p.catalog_product_id}`}>
                    {p.label}
                  </option>
                ))
              : (selectedBrand?.models || []).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
            <option value={OTHER}>{t("tickets.create.option_other")}</option>
          </select>
        </_Field>

        {form.equipment_model_choice === OTHER ? (
          <_Field testId="rl-ticket-create-field-model-custom" label={t("tickets.create.model_custom_label")} htmlFor="rl-ticket-create-model-custom" hint={t("tickets.create.anti_pii_hint")}>
            <input
              id="rl-ticket-create-model-custom"
              data-testid="rl-ticket-create-model-custom-input"
              type="text"
              value={form.equipment_model_custom}
              onChange={(e) => set("equipment_model_custom", e.target.value)}
              placeholder={t("tickets.create.model_custom_placeholder")}
              maxLength={120}
              className={INPUT_CLS}
            />
          </_Field>
        ) : null}

        {form.equipment_product_type === "accessory" ? (
          <_Field testId="rl-ticket-create-field-accessory" label={t("tickets.create.accessory_label")} htmlFor="rl-ticket-create-accessory" hint={t("tickets.create.anti_pii_hint")}>
            <input
              id="rl-ticket-create-accessory"
              data-testid="rl-ticket-create-accessory-input"
              type="text"
              value={form.equipment_accessory_label}
              onChange={(e) => set("equipment_accessory_label", e.target.value)}
              placeholder={t("tickets.create.accessory_placeholder")}
              maxLength={120}
              className={INPUT_CLS}
            />
          </_Field>
        ) : null}

        <_Field testId="rl-ticket-create-field-equipment" label={t("tickets.create.equipment_label")} htmlFor="rl-ticket-create-equipment" hint={t("tickets.create.anti_pii_hint")}>
          <input
            id="rl-ticket-create-equipment"
            data-testid="rl-ticket-create-equipment-input"
            type="text"
            value={form.equipment_reference_label}
            onChange={(e) => set("equipment_reference_label", e.target.value)}
            placeholder={t("tickets.create.equipment_placeholder")}
            maxLength={200}
            className={INPUT_CLS}
            required
          />
        </_Field>

        {/* ============ SS-2.1 · Stringing ============ */}
        <_SectionTitle testId="rl-ticket-create-section-stringing">
          {t("tickets.create.section_stringing_title")}
        </_SectionTitle>

        <datalist id="rl-strings-datalist">
          {catalogStrings.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>

        <_Field testId="rl-ticket-create-field-string-main" label={t("tickets.create.string_main_label")} htmlFor="rl-ticket-create-string-main" hint={t("tickets.create.anti_pii_hint")}>
          {centralStrings.length > 0 && (
            <select
              data-testid="rl-ticket-create-string-main-catalog-select"
              value={form.string_main_catalog_product_id}
              onChange={(e) => onStringCatalogPick("main", e.target.value)}
              className={`${INPUT_CLS} mb-1`}
            >
              <option value="">{t("tickets.create.string_catalog_placeholder")}</option>
              {centralStrings.map((p) => (
                <option key={p.catalog_product_id} value={p.catalog_product_id}>{p.label}</option>
              ))}
            </select>
          )}
          <input
            id="rl-ticket-create-string-main"
            data-testid="rl-ticket-create-string-main-input"
            type="text"
            list="rl-strings-datalist"
            value={form.string_main_label}
            onChange={(e) => setForm((s) => ({
              ...s, string_main_label: e.target.value, string_main_catalog_product_id: "",
            }))}
            placeholder={t("tickets.create.string_main_placeholder")}
            maxLength={80}
            className={INPUT_CLS}
          />
        </_Field>

        <_Field testId="rl-ticket-create-field-string-cross" label={t("tickets.create.string_cross_label")} htmlFor="rl-ticket-create-string-cross">
          {centralStrings.length > 0 && (
            <select
              data-testid="rl-ticket-create-string-cross-catalog-select"
              value={form.string_cross_catalog_product_id}
              onChange={(e) => onStringCatalogPick("cross", e.target.value)}
              className={`${INPUT_CLS} mb-1`}
            >
              <option value="">{t("tickets.create.string_catalog_placeholder")}</option>
              {centralStrings.map((p) => (
                <option key={p.catalog_product_id} value={p.catalog_product_id}>{p.label}</option>
              ))}
            </select>
          )}
          <div className="flex gap-1">
            <input
              id="rl-ticket-create-string-cross"
              data-testid="rl-ticket-create-string-cross-input"
              type="text"
              list="rl-strings-datalist"
              value={form.string_cross_label}
              onChange={(e) => setForm((s) => ({
                ...s, string_cross_label: e.target.value, string_cross_catalog_product_id: "",
              }))}
              placeholder={t("tickets.create.string_cross_placeholder")}
              maxLength={80}
              className={`${INPUT_CLS} flex-1`}
            />
            <button
              type="button"
              data-testid="rl-ticket-create-string-copy-main"
              onClick={() => setForm((s) => ({
                ...s,
                string_cross_label: s.string_main_label,
                string_cross_catalog_product_id: s.string_main_catalog_product_id,
              }))}
              title={t("tickets.create.string_copy_main")}
              className="rounded-md border border-slate-300 px-2 py-1 text-[10px] text-slate-600 hover:bg-slate-50"
            >
              {t("tickets.create.string_copy_main")}
            </button>
          </div>
        </_Field>

        <div className="grid grid-cols-3 gap-2 md:col-span-2">
          {shopPresetApplied && (
            <p
              data-testid="rl-ticket-create-tension-preset-banner"
              className="md:col-span-3 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] text-emerald-800"
            >
              {t("tickets.create.tension_preset_banner")}
            </p>
          )}
          {operatorPresetApplied && (
            <p
              data-testid="rl-ticket-create-tension-preset-banner-operator"
              className="md:col-span-3 rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-[10px] text-sky-800"
            >
              {t("tickets.create.tension_preset_banner_operator")}
            </p>
          )}
          <_Field testId="rl-ticket-create-field-tension-main" label={t("tickets.create.tension_main_label")} htmlFor="rl-ticket-create-tension-main">
            <input
              id="rl-ticket-create-tension-main"
              data-testid="rl-ticket-create-tension-main-input"
              type="number"
              min={1}
              max={80}
              value={form.tension_main}
              onChange={(e) => set("tension_main", e.target.value)}
              placeholder={t("tickets.create.tension_placeholder")}
              className={INPUT_CLS}
            />
          </_Field>
          <_Field testId="rl-ticket-create-field-tension-cross" label={t("tickets.create.tension_cross_label")} htmlFor="rl-ticket-create-tension-cross">
            <input
              id="rl-ticket-create-tension-cross"
              data-testid="rl-ticket-create-tension-cross-input"
              type="number"
              min={1}
              max={80}
              value={form.tension_cross}
              onChange={(e) => set("tension_cross", e.target.value)}
              placeholder={t("tickets.create.tension_placeholder")}
              className={INPUT_CLS}
            />
          </_Field>
          <_Field testId="rl-ticket-create-field-tension-unit" label={t("tickets.create.tension_unit_label")} htmlFor="rl-ticket-create-tension-unit">
            <select
              id="rl-ticket-create-tension-unit"
              data-testid="rl-ticket-create-tension-unit-select"
              value={form.tension_unit}
              onChange={(e) => set("tension_unit", e.target.value)}
              className={INPUT_CLS}
            >
              <option value="kg">kg</option>
              <option value="lbs">lbs</option>
            </select>
          </_Field>
        </div>

        <_Field testId="rl-ticket-create-field-string-origin" label={t("tickets.create.string_origin_label")} htmlFor="rl-ticket-create-string-origin">
          <select
            id="rl-ticket-create-string-origin"
            data-testid="rl-ticket-create-string-origin-select"
            value={form.string_origin}
            onChange={(e) => setForm((s) => ({ ...s, string_origin: e.target.value, string_supply_format: "", customer_string_notes: "" }))}
            className={INPUT_CLS}
          >
            <option value="">{t("tickets.create.string_origin_placeholder")}</option>
            <option value="shop">{t("tickets.stringing.origin_shop")}</option>
            <option value="customer">{t("tickets.stringing.origin_customer")}</option>
          </select>
        </_Field>

        {form.string_origin === "shop" ? (
          <_Field testId="rl-ticket-create-field-inventory" label={t("tickets.create.inventory_item_label")} htmlFor="rl-ticket-create-inventory" hint={t("tickets.create.inventory_item_hint")}>
            <select
              id="rl-ticket-create-inventory"
              data-testid="rl-ticket-create-inventory-select"
              value={form.racket_lab_inventory_item_id}
              onChange={(e) => set("racket_lab_inventory_item_id", e.target.value)}
              className={INPUT_CLS}
              disabled={!form.racket_lab_shop_id}
            >
              <option value="">{t("tickets.create.inventory_item_placeholder")}</option>
              {inventoryItems.map((it) => (
                <option key={it.id} value={it.id}>
                  {`${it.brand_label} ${it.model_label} · ${it.available_quantity} ${it.unit}`}
                </option>
              ))}
            </select>
          </_Field>
        ) : null}

        {form.string_origin === "customer" ? (
          <>
            <_Field testId="rl-ticket-create-field-supply-format" label={t("tickets.create.supply_format_label")} htmlFor="rl-ticket-create-supply-format">
              <select
                id="rl-ticket-create-supply-format"
                data-testid="rl-ticket-create-supply-format-select"
                value={form.string_supply_format}
                onChange={(e) => set("string_supply_format", e.target.value)}
                className={INPUT_CLS}
              >
                <option value="">{t("tickets.create.supply_format_placeholder")}</option>
                <option value="set">{t("tickets.stringing.supply_set")}</option>
                <option value="reel">{t("tickets.stringing.supply_reel")}</option>
              </select>
            </_Field>
            <_Field testId="rl-ticket-create-field-customer-string-notes" label={t("tickets.create.customer_string_notes_label")} htmlFor="rl-ticket-create-customer-string-notes" hint={t("tickets.create.anti_pii_hint")} className="md:col-span-2">
              <textarea
                id="rl-ticket-create-customer-string-notes"
                data-testid="rl-ticket-create-customer-string-notes-input"
                value={form.customer_string_notes}
                onChange={(e) => set("customer_string_notes", e.target.value)}
                placeholder={t("tickets.create.customer_string_notes_placeholder")}
                maxLength={500}
                rows={2}
                className={INPUT_CLS}
              />
            </_Field>
          </>
        ) : null}

        {/* ============ SS-2.1 · Context ============ */}
        <_SectionTitle testId="rl-ticket-create-section-context">
          {t("tickets.create.section_context_title")}
        </_SectionTitle>

        <_Field testId="rl-ticket-create-field-customer" label={t("tickets.create.customer_label")} htmlFor="rl-ticket-create-customer" hint={t("tickets.create.anti_pii_hint")}>
          <input
            id="rl-ticket-create-customer"
            data-testid="rl-ticket-create-customer-input"
            type="text"
            value={form.customer_reference_label}
            onChange={(e) => set("customer_reference_label", e.target.value)}
            placeholder={t("tickets.create.customer_placeholder")}
            maxLength={120}
            className={INPUT_CLS}
          />
        </_Field>

        <_Field testId="rl-ticket-create-field-profile-type" label={t("tickets.create.profile_type_label")} htmlFor="rl-ticket-create-profile-type" hint={t("tickets.create.profile_type_hint")}>
          <select
            id="rl-ticket-create-profile-type"
            data-testid="rl-ticket-create-profile-type-select"
            value={form.created_for_profile_type}
            onChange={(e) => set("created_for_profile_type", e.target.value)}
            className={INPUT_CLS}
          >
            <option value="">{t("tickets.create.profile_type_placeholder")}</option>
            {PROFILE_TYPES.map((p) => (
              <option key={p} value={p}>{t(`tickets.stringing.profile_${p}`)}</option>
            ))}
          </select>
        </_Field>

        <_Field testId="rl-ticket-create-field-scheduled" label={t("tickets.create.scheduled_label")} htmlFor="rl-ticket-create-scheduled" hint={t("tickets.create.scheduled_hint")}>
          <input
            id="rl-ticket-create-scheduled"
            data-testid="rl-ticket-create-scheduled-input"
            type="date"
            value={form.scheduled_for}
            onChange={(e) => set("scheduled_for", e.target.value)}
            className={INPUT_CLS}
          />
        </_Field>

        <_Field testId="rl-ticket-create-field-operator" label={t("tickets.create.operator_label")} htmlFor="rl-ticket-create-operator" hint={t("tickets.create.operator_hint")}>
          {/* RACKET-LAB-ROLE-4A (Feb 2026) — typeahead/select scoped by
              shop. RACKET-LAB-ROLE-4C (Feb 2026) — promoted to a real
              debounced typeahead with server-side `q` filtering. The
              "Use UUID" toggle preserves the legacy manual flow as a
              safety fallback (admin-only surface). When no shop is
              selected, the field is disabled. */}
          {operatorManualMode ? (
            <input
              id="rl-ticket-create-operator"
              data-testid="rl-ticket-create-operator-input"
              type="text"
              value={form.racket_lab_operator_id}
              onChange={(e) => onOperatorChange(e.target.value)}
              placeholder={t("tickets.create.operator_placeholder")}
              maxLength={64}
              className={INPUT_CLS}
            />
          ) : (
            <div
              ref={operatorContainerRef}
              data-testid="rl-ticket-create-operator-typeahead"
              className="relative"
            >
              {selectedOperator && form.racket_lab_operator_id ? (
                <div
                  data-testid="rl-ticket-create-operator-chip"
                  className={`${INPUT_CLS} flex items-center justify-between gap-2 bg-slate-50`}
                >
                  <span
                    data-testid="rl-ticket-create-operator-chip-label"
                    className="truncate text-slate-700"
                  >
                    {selectedOperator.display_label || form.racket_lab_operator_id}
                    {selectedOperator.is_placeholder ? (
                      <span className="ml-1 text-[10px] text-slate-400">
                        · {t("tickets.create.operator_placeholder_tag")}
                      </span>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    data-testid="rl-ticket-create-operator-clear"
                    onClick={() => {
                      onOperatorChange("");
                      setOperatorSearch("");
                      setOperatorSearchDebounced("");
                      setOperatorDropdownOpen(false);
                    }}
                    className="text-slate-400 hover:text-slate-700"
                    aria-label={t("tickets.create.operator_clear")}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <input
                  id="rl-ticket-create-operator"
                  data-testid="rl-ticket-create-operator-search-input"
                  type="text"
                  value={operatorSearch}
                  onFocus={() => setOperatorDropdownOpen(true)}
                  onChange={(e) => {
                    setOperatorSearch(e.target.value);
                    setOperatorDropdownOpen(true);
                  }}
                  placeholder={
                    !form.racket_lab_shop_id
                      ? t("tickets.create.operator_pick_shop_first")
                      : t("tickets.create.operator_search_placeholder")
                  }
                  disabled={
                    !form.racket_lab_shop_id || rlCtx?.profile === "qa_tester"
                  }
                  maxLength={120}
                  className={`${INPUT_CLS} w-full`}
                />
              )}

              {operatorDropdownOpen &&
              form.racket_lab_shop_id &&
              rlCtx?.profile !== "qa_tester" &&
              !(selectedOperator && form.racket_lab_operator_id) ? (
                <div
                  data-testid="rl-ticket-create-operator-dropdown"
                  className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-auto rounded-md border border-slate-200 bg-white shadow-md"
                >
                  {opsLoading ? (
                    <p
                      data-testid="rl-ticket-create-operator-dropdown-loading"
                      className="px-2 py-1.5 text-[11px] text-slate-400"
                    >
                      {t("tickets.create.operator_loading")}
                    </p>
                  ) : operatorOptions.length === 0 ? (
                    <p
                      data-testid="rl-ticket-create-operator-dropdown-empty"
                      className="px-2 py-1.5 text-[11px] text-slate-400"
                    >
                      {operatorSearchDebounced.trim()
                        ? t("tickets.create.operator_no_results")
                        : t("tickets.create.operator_empty_shop")}
                    </p>
                  ) : (
                    <ul className="py-0.5">
                      {operatorOptions.map((op) => (
                        <li key={op.id}>
                          <button
                            type="button"
                            data-testid={`rl-ticket-create-operator-option-${op.id}`}
                            onClick={() => {
                              onOperatorChange(op.id, op);
                              setOperatorSearch("");
                              setOperatorSearchDebounced("");
                              setOperatorDropdownOpen(false);
                            }}
                            className="block w-full truncate px-2 py-1.5 text-left text-[12px] text-slate-700 hover:bg-slate-50"
                          >
                            {op.display_label || op.id}
                            {op.is_placeholder ? (
                              <span className="ml-1 text-[10px] text-slate-400">
                                · {t("tickets.create.operator_placeholder_tag")}
                              </span>
                            ) : null}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}
            </div>
          )}
          <button
            type="button"
            data-testid="rl-ticket-create-operator-mode-toggle"
            onClick={() => {
              setOperatorManualMode((v) => !v);
              setOperatorDropdownOpen(false);
            }}
            className="mt-1 text-[10px] text-slate-500 underline hover:text-slate-700"
          >
            {operatorManualMode
              ? t("tickets.create.operator_use_select")
              : t("tickets.create.operator_use_manual")}
          </button>
        </_Field>

        <_Field testId="rl-ticket-create-field-machine" label={t("tickets.create.machine_label")} htmlFor="rl-ticket-create-machine">
          <input
            id="rl-ticket-create-machine"
            data-testid="rl-ticket-create-machine-input"
            type="text"
            value={form.racket_lab_machine_id}
            onChange={(e) => set("racket_lab_machine_id", e.target.value)}
            placeholder={t("tickets.create.machine_placeholder")}
            maxLength={64}
            className={INPUT_CLS}
          />
        </_Field>

        <_Field testId="rl-ticket-create-field-notes" label={t("tickets.create.notes_label")} htmlFor="rl-ticket-create-notes" hint={t("tickets.create.anti_pii_hint")} className="md:col-span-2">
          <textarea
            id="rl-ticket-create-notes"
            data-testid="rl-ticket-create-notes-input"
            value={form.equipment_notes}
            onChange={(e) => set("equipment_notes", e.target.value)}
            placeholder={t("tickets.create.notes_placeholder")}
            maxLength={2000}
            rows={2}
            className={INPUT_CLS}
          />
        </_Field>

        {error ? (
          <div data-testid="rl-ticket-create-error" className="md:col-span-2 rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
            {error}
          </div>
        ) : null}

        <div className="md:col-span-2 flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
          <p data-testid="rl-ticket-create-initial-status-hint" className="text-[10px] text-slate-500">
            {form.racket_lab_operator_id.trim()
              ? t("tickets.create.initial_status_hint_assigned")
              : t("tickets.create.initial_status_hint_draft")}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="rl-ticket-create-cancel"
              onClick={onClose}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
            >
              {t("tickets.create.cancel")}
            </button>
            <button
              type="submit"
              data-testid="rl-ticket-create-submit"
              disabled={!required_ok || submitting}
              className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? t("tickets.create.submitting") : t("tickets.create.submit")}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

const TicketCreateDialog = ({ open, onClose, onCreated }) => {
  if (!open) return null;
  return (
    <div data-testid="rl-ticket-create-dialog" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <TicketCreateDialogInner onClose={onClose} onCreated={onCreated} />
    </div>
  );
};

export default TicketCreateDialog;
export { TicketCreateDialog };
